import React, { useState } from "react";
import {
  Stack,
  Text,
  Link,
  mergeStyleSets,
  getTheme,
  PrimaryButton,
  DefaultButton,
} from "@fluentui/react";
import useAppInsights from "../../../Hooks/UseAppInsights";
import { NavType } from "../../../Shared/AppInsights";

interface AdvanceDialogManualInstructionsProps {
  /**
   * The URL link to the latest Ev2 Portal rollout page.
   * This link will be opened in a new tab when users click on it in step 1.
   */
  latestEv2PortalLink: string;
}

/**
 * A step-by-step wizard component that provides visual instructions for manually promoting
 * an Ev2 rollout stage. Used when users select "Manual" progression type in the AdvanceDialog.
 */
const AdvanceDialogManualInstructions: React.FC<
  AdvanceDialogManualInstructionsProps
> = ({ latestEv2PortalLink }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { trackOperationEvent, trackNavigationEvent } = useAppInsights(
    "AdvanceDialogManualInstructions",
  );

  const steps = [
    {
      title: "Open Ev2 Rollout",
      content: (
        <Text>
          Within an <strong>AME</strong> account or <strong>SAW</strong>, open
          the latest{" "}
          <Link
            href={latestEv2PortalLink}
            target="_blank"
            onClick={() => {
              trackNavigationEvent({
                destination: latestEv2PortalLink,
                navType: NavType.External,
              });
            }}
          >
            Ev2 rollout
          </Link>
        </Text>
      ),
      image: "advancedDialog_step1.png", // Screenshot showing the Ev2 portal overview
    },
    {
      title: "Find Wait/Promotion-1 Stage",
      content: (
        <Text>
          Locate and select the context menu (⋯) for the latest{" "}
          <strong>Wait/Promotion-1</strong> stage that shows{" "}
          <strong>Waiting on Manual Promotion</strong>
        </Text>
      ),
      image: "advancedDialog_step2.png", // Screenshot highlighting the Wait/Promotion-1 stage and context menu
    },
    {
      title: "Manually Promote",
      content: (
        <Text>
          From the context menu, select <strong>Manually Promote</strong> and
          confirm the action
        </Text>
      ),
      image: "advancedDialog_step3.png", // Screenshot showing the "Manually Promote" option in the context menu
    },
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
    trackOperationEvent({
      operation: "NextStep",
      function: "nextStep",
      properties: {
        nextStep: currentStep + 1,
        stepTitle: steps[currentStep + 1].title,
      },
    });
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
    trackOperationEvent({
      operation: "PreviousStep",
      function: "prevStep",
      properties: {
        previousStep: currentStep - 1,
        stepTitle: steps[currentStep - 1].title,
      },
    });
  };

  const goToStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
    trackOperationEvent({
      operation: "GoToStep",
      function: "goToStep",
      properties: {
        stepIndex,
        stepTitle: steps[stepIndex].title,
      },
    });
  };

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Text variant="mediumPlus" className={styles.title}>
        Manual Promotion Instructions
      </Text>

      {/* Current Step Display */}
      <Stack tokens={{ childrenGap: 16 }} className={styles.stepContainer}>
        <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
          <div className={styles.stepNumber}>{currentStep + 1}</div>
          <Text variant="large" className={styles.stepTitle}>
            {steps[currentStep].title}
          </Text>
        </Stack>

        {/* Step Content */}
        <Stack tokens={{ childrenGap: 16 }}>
          {steps[currentStep].content}

          {/* Image */}
          <div className={styles.imageContainer}>
            <div className={styles.imagePlaceholder}>
              <img
                src={`/images/${steps[currentStep].image}`}
                alt={steps[currentStep].title}
                className={styles.stepImage}
              />
            </div>
          </div>
        </Stack>

        {/* Navigation Buttons */}
        <Stack
          horizontal
          tokens={{ childrenGap: 8 }}
          horizontalAlign="space-between"
        >
          <DefaultButton
            text="Previous"
            iconProps={{ iconName: "ChevronLeft" }}
            onClick={prevStep}
            disabled={currentStep === 0}
          />

          {/* Progress Indicators */}
          <Stack
            horizontal
            tokens={{ childrenGap: 8 }}
            horizontalAlign="center"
          >
            {steps.map((_, index) => (
              <div
                key={index}
                className={
                  index === currentStep ? styles.dotActive : styles.dot
                }
                onClick={() => goToStep(index)}
                role="button"
                tabIndex={0}
              />
            ))}
          </Stack>

          {currentStep < steps.length - 1 ? (
            <PrimaryButton
              text="Next"
              iconProps={{ iconName: "ChevronRight" }}
              onClick={nextStep}
            />
          ) : (
            <PrimaryButton
              text="Complete"
              iconProps={{ iconName: "CheckMark" }}
              onClick={() => {}}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

const theme = getTheme();

const styles = mergeStyleSets({
  title: {
    fontWeight: 600,
  },
  stepTitle: {
    fontWeight: 600,
  },
  stepCounter: {
    alignSelf: "center",
    color: theme.palette.neutralSecondary,
  },
  /** Step screenshot image with full-width responsive sizing */
  stepImage: {
    width: "100%",
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    backgroundColor: theme.palette.themePrimary,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 600,
    flexShrink: 0,
  },
  stepContainer: {
    padding: "16px",
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: "4px",
    backgroundColor: theme.palette.neutralLighterAlt,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor: theme.palette.neutralTertiary,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      backgroundColor: theme.palette.neutralSecondary,
    },
  },
  dotActive: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor: theme.palette.themePrimary,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  imageContainer: {
    width: "100%",
    maxWidth: "600px",
    alignSelf: "center",
    overflow: "hidden", // Ensure clean cropping
  },
  imagePlaceholder: {
    width: "100%",
    border: `2px dashed ${theme.palette.neutralTertiary}`,
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.neutralLighter,
    padding: 5,
    overflow: "hidden",
  },
});

export default AdvanceDialogManualInstructions;
